import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, Globe, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BRAND, SERVICES } from "@/constants/brand";

// PHP mail endpoint on your own server, e.g. https://infolocus.in/sendmail.php
const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || "/sendmail.php";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!emailRegex.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Please enter your phone";
    if (!form.message.trim()) e.message = "Tell us a little about your needs";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(CONTACT_ENDPOINT, form, { timeout: 25000 });
      if (res.data?.status !== "ok") {
        throw new Error(res.data?.message || "Submission failed");
      }
      toast.success(res.data?.message || "Thanks — we'll be in touch shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again or WhatsApp us.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="uppercase text-xs tracking-[0.22em] font-bold text-[#0055FF]">
              Let's talk
            </p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl tracking-tight font-bold text-[#0B1120] leading-tight">
              Book a free consultation.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#475569] leading-relaxed">
              Share a few details about your business. Our team will reach out within
              one business day to scope the right engagement for you.
            </p>

            <div className="mt-10 space-y-5">
              <ContactRow
                icon={<Phone className="h-4 w-4" />}
                label="Phone"
                value={BRAND.phone}
                href={`tel:${BRAND.phone.replace(/[^+\d]/g, "")}`}
                testid="contact-info-phone"
              />
              <ContactRow
                icon={<Mail className="h-4 w-4" />}
                label="Email"
                value={BRAND.emailPublic}
                href={`mailto:${BRAND.emailPublic}`}
                testid="contact-info-email"
              />
              <ContactRow
                icon={<Globe className="h-4 w-4" />}
                label="Website"
                value={BRAND.website}
                href={BRAND.websiteUrl}
                testid="contact-info-website"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              noValidate
              className="bg-[#FAFAFA] border border-[#E2E8F0] rounded-sm p-7 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full name" error={errors.name}>
                  <Input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    placeholder="Your name"
                    className="bg-white border-[#E2E8F0] h-11 rounded-sm"
                  />
                </Field>
                <Field label="Work email" error={errors.email}>
                  <Input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    placeholder="you@company.com"
                    className="bg-white border-[#E2E8F0] h-11 rounded-sm"
                  />
                </Field>
                <Field label="Phone" error={errors.phone}>
                  <Input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    placeholder="+91 ••••• •••••"
                    className="bg-white border-[#E2E8F0] h-11 rounded-sm"
                  />
                </Field>
                <Field label="Company" error={errors.company}>
                  <Input
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={(e) => setField("company", e.target.value)}
                    placeholder="Company name"
                    className="bg-white border-[#E2E8F0] h-11 rounded-sm"
                  />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Service of interest" error={errors.service}>
                    <Select
                      value={form.service}
                      onValueChange={(v) => setField("service", v)}
                    >
                      <SelectTrigger
                        data-testid="contact-select-service"
                        className="bg-white border-[#E2E8F0] h-11 rounded-sm"
                      >
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem
                            key={s.id}
                            value={s.title}
                            data-testid={`contact-select-option-${s.id}`}
                          >
                            {s.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="Not sure yet" data-testid="contact-select-option-other">
                          Not sure yet — advise me
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Message" error={errors.message}>
                    <Textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={(e) => setField("message", e.target.value)}
                      placeholder="What are you trying to solve?"
                      rows={5}
                      className="bg-white border-[#E2E8F0] rounded-sm"
                    />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="mt-7 group inline-flex items-center gap-2 bg-[#1B2A6E] disabled:opacity-60 disabled:cursor-not-allowed text-white hover:bg-[#152259] px-7 py-4 rounded-sm text-sm font-semibold tracking-wide transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send enquiry
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="mt-3 text-xs text-[#475569]">
                By submitting, you agree to be contacted at the details provided.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B1120]">
      {label}
    </Label>
    <div className="mt-2">{children}</div>
    {error && (
      <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">
        {error}
      </p>
    )}
  </div>
);

const ContactRow = ({ icon, label, value, href, testid }) => (
  <a
    href={href}
    target={href?.startsWith("http") ? "_blank" : undefined}
    rel="noreferrer"
    data-testid={testid}
    className="flex items-center gap-4 group"
  >
    <div className="h-10 w-10 flex items-center justify-center border border-[#E2E8F0] bg-white rounded-sm text-[#1B2A6E] group-hover:bg-[#1B2A6E] group-hover:text-white transition-colors">
      {icon}
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#475569]">
        {label}
      </div>
      <div className="text-base font-semibold text-[#0B1120] group-hover:text-[#1B2A6E] transition-colors">
        {value}
      </div>
    </div>
  </a>
);
